import { useCallback, useState } from "react";
import { API } from "../../../services/api";
import { ADMIN_API_CONFIG, GUARDIAN_CURATOR_FIELDS } from "../model/administrationConstants";

// Tableau éditable ligne par ligne : chaque ligne modifiée garde un brouillon jusqu'à son enregistrement.
function useRowDrafts(rowKey, onError) {
    const [drafts, setDrafts] = useState({});
    const [savingIds, setSavingIds] = useState({});
    const updateDraft = (row, patch) => {
        setDrafts(current => ({ ...current, [row[rowKey]]: { ...(current[row[rowKey]] || row), ...patch } }));
        onError("");
    };
    const cancelDraft = id => {
        setDrafts(current => {
            const next = { ...current };
            delete next[id];
            return next;
        });
    };
    const setSaving = (id, saving) => setSavingIds(current => ({ ...current, [id]: saving }));
    return { drafts, setDrafts, savingIds, setSaving, updateDraft, cancelDraft };
}

function useRemoteRows(url, failureMessage, onError) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const load = useCallback(async () => {
        setLoading(true);
        onError("");
        try {
            const response = await API.get(url, ADMIN_API_CONFIG);
            setRows(response.data);
        } catch (requestError) {
            onError(requestError.response?.data?.message || failureMessage);
        } finally {
            setLoading(false);
        }
    }, [failureMessage, onError, url]);
    return { rows, setRows, loading, load };
}

// Suivi des crédits EHPAD : montant, envoi, relance, et ajout d'un montant au crédit total.
export function useEhpadCredits(onError) {
    const remote = useRemoteRows("/administration/ehpad-credits", "Impossible de charger le suivi des crédits EHPAD.", onError);
    const table = useRowDrafts("ehpadId", onError);
    const [additions, setAdditions] = useState({});

    const putCredit = async (ehpadId, body, failureMessage, onSaved) => {
        table.setSaving(ehpadId, true);
        onError("");
        try {
            const response = await API.put(`/administration/ehpads/${ehpadId}/credit`, body, ADMIN_API_CONFIG);
            remote.setRows(current => current.map(row => row.ehpadId === ehpadId ? response.data : row));
            onSaved();
        } catch (requestError) {
            onError(requestError.response?.data?.message || failureMessage);
        } finally {
            table.setSaving(ehpadId, false);
        }
    };

    const saveRow = async ehpadId => {
        const draft = table.drafts[ehpadId];
        if (!draft) return;
        await putCredit(ehpadId, {
            version: draft.version ?? null,
            amount: draft.amount === "" || draft.amount == null ? null : draft.amount,
            sentDate: draft.sentDate || null,
            remark: draft.remark || null,
            followUpDate: draft.followUpDate || null,
        }, "Impossible d'enregistrer le suivi crédit.", () => table.cancelDraft(ehpadId));
    };

    const addAmount = async ehpadId => {
        const row = remote.rows.find(item => item.ehpadId === ehpadId);
        const addition = Number(additions[ehpadId]);
        if (!row || !Number.isFinite(addition) || addition <= 0) return;
        const amount = Math.round((Number(row.amount || 0) + addition) * 100) / 100;
        await putCredit(ehpadId, {
            version: row.version ?? null,
            amount: String(amount),
            sentDate: row.sentDate || null,
            remark: row.remark || null,
            followUpDate: row.followUpDate || null,
        }, "Impossible d'ajouter ce montant au crédit.", () => setAdditions(current => ({ ...current, [ehpadId]: "" })));
    };

    return {
        ...remote,
        ...table,
        additions,
        setAddition: (ehpadId, value) => setAdditions(current => ({ ...current, [ehpadId]: value })),
        saveRow,
        addAmount,
    };
}

// Tableau Tuteur Curateur.
export function useGuardianCurators(onError) {
    const remote = useRemoteRows("/administration/guardian-curators", "Impossible de charger le tableau Tuteur Curateur.", onError);
    const table = useRowDrafts("id", onError);

    const saveRow = async id => {
        const draft = table.drafts[id];
        if (!draft) return;
        table.setSaving(id, true);
        onError("");
        try {
            const payload = Object.fromEntries([
                ["version", draft.version ?? null],
                ...GUARDIAN_CURATOR_FIELDS.map(([field]) => [field, draft[field] || null]),
            ]);
            const response = await API.put(`/administration/guardian-curators/${id}`, payload, ADMIN_API_CONFIG);
            remote.setRows(current => current.map(row => row.id === id ? response.data : row));
            table.cancelDraft(id);
        } catch (requestError) {
            onError(requestError.response?.data?.message || "Impossible d'enregistrer cette ligne Tuteur Curateur.");
        } finally {
            table.setSaving(id, false);
        }
    };

    return { ...remote, ...table, saveRow };
}
